import { FC, useCallback, useEffect, useId, useState } from 'react';

import {
  BookmarkIcon,
  CheckCircleIcon,
  ClipboardCopyIcon,
  ShareIcon,
} from '@heroicons/react/outline';
import { Icon } from '@shared/components/icons';

import { ContactEmailLink } from '../../components/ContactEmailLink';
import CouponProductCard from './CouponProductCard';

interface SaveLinkViewProps {
  expiresAt: Date;
  linkUrl: string;
  imageUrl: string;
  itemName: string;
  couponCode: string;
  brandName: string | null;
  selectedUnit: { name: string; image?: { url?: string } } | null;
}

const SaveLinkView: FC<SaveLinkViewProps> = ({
  expiresAt,
  linkUrl,
  imageUrl,
  itemName,
  couponCode,
  brandName,
  selectedUnit,
}) => {
  const [copied, setCopied] = useState(false);
  const { ShareKakao } = Icon();
  const kakaoButtonId = useId().replace(/:/g, '');

  const expiryText = new Date(expiresAt).toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const shareText = `선물교환신청(유효기간 ${expiryText})`;
  const clipboardContent = `${shareText}\n${linkUrl}`;

  useEffect(() => {
    const kakao = window.Kakao;
    if (!kakao) return;

    if (!kakao.isInitialized()) {
      kakao.init(process.env.REACT_APP_KAKAO_SHARE_JAVASCRIPT_KEY);
    }

    kakao.Share.cleanup();
    kakao.Share.createDefaultButton({
      container: `#kakao-save-link-${kakaoButtonId}`,
      objectType: 'text',
      text: `${shareText}\n\n유효기간 내에 아래 링크를 열어 신청해 주세요.`,
      buttonTitle: '선물 교환 신청하기',
      link: {
        webUrl: linkUrl,
        mobileWebUrl: linkUrl,
      },
    });
  }, [shareText, linkUrl, kakaoButtonId]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(clipboardContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = clipboardContent;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  }, [clipboardContent]);

  const handleShare = useCallback(async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: '선물교환신청',
        text: shareText,
        url: linkUrl,
      });
    } catch {
      // 사용자가 공유를 취소한 경우 무시
    }
  }, [shareText, linkUrl]);

  const supportsShare = typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white px-4 py-8">
      <div className="mx-auto max-w-md">
        <div className="rounded-xl border border-stone-200 bg-white p-6 text-center">
          <BookmarkIcon className="mx-auto h-10 w-10 text-stone-500" />
          <h2 className="mt-3 text-lg font-bold text-stone-800">링크를 저장해 주세요</h2>
          <p className="mt-2 text-sm leading-relaxed text-stone-500">
            이 링크를 저장해 두시면{' '}
            <span className="font-bold text-stone-700">~{expiryText}</span> 내 언제든
            신청하실 수 있습니다.
          </p>

          <div className="mt-5 rounded-lg border border-stone-200 bg-stone-50 px-4 py-3">
            <p className="break-all text-xs text-stone-500">{linkUrl}</p>
          </div>

          <div className="mt-4 space-y-2">
            <button
              onClick={handleCopy}
              className={`flex w-full items-center justify-center gap-2 rounded-lg py-3.5 text-sm font-bold transition ${
                copied
                  ? 'border border-green-300 bg-green-50 text-green-700'
                  : 'bg-stone-800 text-white active:bg-stone-900'
              }`}
            >
              {copied ? (
                <>
                  <CheckCircleIcon className="h-5 w-5" />
                  복사 완료!
                </>
              ) : (
                <>
                  <ClipboardCopyIcon className="h-5 w-5" />
                  링크 복사
                </>
              )}
            </button>

            <button
              id={`kakao-save-link-${kakaoButtonId}`}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#FEE500] py-3.5 text-sm font-bold text-[#191919] transition active:bg-[#F5DC00]"
            >
              <ShareKakao className="h-5 w-5" />
              카카오톡 나에게 보내기
            </button>

            {supportsShare && (
              <button
                onClick={handleShare}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-stone-300 bg-white py-3.5 text-sm font-bold text-stone-700 transition active:bg-stone-50"
              >
                <ShareIcon className="h-5 w-5" />
                다른 앱으로 공유
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <p className="text-xs font-bold text-blue-800">저장 팁</p>
          <ul className="mt-1.5 space-y-1 text-xs text-blue-700">
            <li>• 카카오톡 &quot;나에게 보내기&quot;로 링크를 저장하세요</li>
            <li>• 또는 메모앱에 붙여넣어 보관하세요</li>
            <li>• 유효기간 내 이 링크를 다시 열면 신청하실 수 있습니다</li>
          </ul>
        </div>

        <CouponProductCard
          imageUrl={imageUrl}
          itemName={itemName}
          couponCode={couponCode}
          brandName={brandName}
          selectedUnit={selectedUnit}
        />

        <ContactEmailLink />
      </div>
    </div>
  );
};

export default SaveLinkView;
